/**
 * Quote list store.
 *
 * A localStorage-backed singleton that holds "quote only" products the customer
 * wants a quote for. Completely separate from the Shopify cart — items here never
 * touch /cart/add.js, so they can never be purchased/paid for. Mirrors the shape of
 * the theme's cart events (see @theme/events) but stays self-contained.
 *
 * Subscribe with: document.addEventListener('quote:update', (e) => e.detail.items)
 */

/** @constant {string} localStorage key. Keep in sync with the inline clear script in blocks/contact-form.liquid. */
export const QUOTE_STORAGE_KEY = 'pss-quote';

/**
 * Event dispatched on `document` whenever the quote list changes.
 * @extends {CustomEvent}
 */
export class QuoteUpdateEvent extends CustomEvent {
  /** @param {Array<Object>} items */
  constructor(items) {
    super(QuoteUpdateEvent.eventName, {
      bubbles: true,
      detail: {
        items,
        count: items.reduce((total, item) => total + (Number(item.quantity) || 1), 0),
      },
    });
  }

  static eventName = 'quote:update';
}

/**
 * @typedef {Object} QuoteItem
 * @property {string} id - The variant id (string).
 * @property {string} productId - The product id.
 * @property {string} title - Product title.
 * @property {string} [variantTitle] - Variant title (empty for single-variant products).
 * @property {string} [sku] - Variant SKU.
 * @property {string} [image] - Image URL.
 * @property {string} url - Product URL (variant-specific where possible).
 * @property {number} quantity - Quantity requested.
 */

class QuoteListStore {
  /**
   * Reads the raw list from localStorage.
   * @returns {QuoteItem[]}
   */
  #read() {
    try {
      const raw = localStorage.getItem(QUOTE_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (_) {
      return [];
    }
  }

  /**
   * Persists the list and notifies subscribers.
   * @param {QuoteItem[]} items
   */
  #write(items) {
    try {
      localStorage.setItem(QUOTE_STORAGE_KEY, JSON.stringify(items));
    } catch (_) {
      // Storage may be full or disabled (private mode) — still dispatch so the UI reflects intent.
    }
    document.dispatchEvent(new QuoteUpdateEvent(items));
  }

  /** @returns {QuoteItem[]} A copy of the current items. */
  items() {
    return this.#read();
  }

  /** @returns {number} Total quantity across all items (matches cart item_count semantics). */
  count() {
    return this.#read().reduce((total, item) => total + (Number(item.quantity) || 1), 0);
  }

  /**
   * Adds an item, merging quantity if the same variant is already present.
   * @param {QuoteItem} item
   */
  add(item) {
    if (!item || !item.id) return;
    const items = this.#read();
    const quantity = Math.max(1, Number(item.quantity) || 1);
    const existing = items.find((entry) => String(entry.id) === String(item.id));

    if (existing) {
      existing.quantity = (Number(existing.quantity) || 1) + quantity;
    } else {
      items.push({ ...item, id: String(item.id), quantity });
    }

    this.#write(items);
  }

  /**
   * Sets the quantity for a variant. A quantity of 0 (or less) removes it.
   * @param {string} id - Variant id.
   * @param {number} quantity
   */
  updateQuantity(id, quantity) {
    const next = Math.floor(Number(quantity) || 0);
    let items = this.#read();

    if (next <= 0) {
      items = items.filter((entry) => String(entry.id) !== String(id));
    } else {
      const entry = items.find((item) => String(item.id) === String(id));
      if (entry) entry.quantity = next;
    }

    this.#write(items);
  }

  /**
   * Removes a variant from the list.
   * @param {string} id - Variant id.
   */
  remove(id) {
    this.#write(this.#read().filter((entry) => String(entry.id) !== String(id)));
  }

  /** Empties the list. */
  clear() {
    this.#write([]);
  }
}

/** Shared singleton instance. */
export const QuoteList = new QuoteListStore();
